import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { localized } from 'shared/state/i18n/i18n';
import PageContainer from '../../page-container/page-container';
import { Heading } from '../../utils/heading';
import { SkioldFormsWrapper } from '../../skiold-components/skiold-forms-wrapper/skiold-forms-wrapper';
import { FormRow } from '../../skiold-components/skiold-forms-wrapper/skiold-forms-wrapper-types';
import { SkioldFormDropdown } from '../../skiold-components/skiold-dropdown/skiold-form-dropdown';
import { Option } from 'react-dropdown';
import { AnimalType, IRecipeDto, LocationType } from 'shared/api/api';
import { SkioldFormInput } from '../../skiold-components/skiold-input/skiold-form-input';
import { ViewWeb } from '../../utils/web-view';
import { SkioldButton } from '../../skiold-components/skiold-button/skiold-button';
import { useDispatch, useSelector } from 'react-redux';
import { getRecipes, upsertRecipe } from 'shared/state/ducks/recipe/operation';
import { showAlert } from '../../skiold-alert/skiold-alert';
import { selectSiteId } from 'shared/state/ducks/site/selectors';

interface PropsFromParent {
	recipe?: IRecipeDto;
	closeModal?: () => void;
}

export const CreateRecipe: FC<PropsFromParent> = React.memo(({ recipe, closeModal }) => {
	// init
	const dispatch = useDispatch();
	const siteId = useSelector(selectSiteId);
	const [recipeName, setRecipeName] = useState<string>(recipe?.name ?? '');

	// Setup animalType options
	const animalTypeOptions = [
		{ label: ' ', value: '' } as Option,
		{ label: localized(AnimalType.Sow), value: AnimalType.Sow } as Option,
		// { label: localized(AnimalType.YoungFemale), value: AnimalType.YoungFemale } as Option,
		{ label: localized(AnimalType.Weaner), value: AnimalType.Weaner } as Option,
		{ label: localized(AnimalType.Finisher), value: AnimalType.Finisher } as Option,
	];

	const [selectedOption, setSelectedOption] = useState<Option | undefined>(
		recipe?.animalType
			? { label: localized(recipe?.animalType), value: recipe?.animalType }
			: ({ label: ' ', value: '' } as Option),
	);

	const [selectedLocationType, setSelectedLocationType] = useState<Option | undefined>(
		recipe?.locationType
			? { label: localized(recipe?.locationType), value: recipe?.locationType }
			: ({ label: ' ', value: '' } as Option),
	);

	const locationTypeOptions = useMemo(() => {
		if (!selectedOption) {
			return [];
		}
		if (selectedOption.value === AnimalType.Sow) {
			return [
				{ label: ' ', value: '' } as Option,
				{ label: localized(LocationType.Mating), value: LocationType.Mating } as Option,
				{ label: localized(LocationType.Pregnant), value: LocationType.Pregnant } as Option,
				{ label: localized(LocationType.Farrowing), value: LocationType.Farrowing } as Option,
				{ label: localized(LocationType.WeanedPiglets), value: LocationType.WeanedPiglets } as Option,
				{ label: localized(LocationType.YoungFemale), value: LocationType.YoungFemale } as Option,
			];
		}
		if (selectedOption.value === AnimalType.Weaner) {
			return [
				{ label: ' ', value: '' } as Option,
				{ label: localized(LocationType.Weaners), value: LocationType.Weaners } as Option,
			];
		}
		if (selectedOption.value === AnimalType.Finisher) {
			return [
				{ label: ' ', value: '' } as Option,
				{ label: localized(LocationType.Finisher), value: LocationType.Finisher } as Option,
			];
		}
		return [];
	}, [selectedOption]);

	const productionChanged = useCallback((option: Option) => {
		setSelectedOption(option);
		setSelectedLocationType({ label: ' ', value: '' });
	}, []);

	// Form components
	const getWorkListHeader = (): FormRow => {
		return {
			header: localized(recipe?'EditRecipe':'CreateRecipe'),
		};
	};

	const getAnimalTypeRow = (): FormRow => {
		return {
			name: localized('production'),
			component: (
				<SkioldFormDropdown
					items={animalTypeOptions}
					selectedValue={selectedOption}
					onValueChanged={productionChanged}
				/>
			),
		};
	};

	const getLocationTypeRow = (): FormRow => {
		return {
			name: localized('RecipeType'),
			component: (
				<SkioldFormDropdown
					items={locationTypeOptions}
					selectedValue={selectedLocationType}
					onValueChanged={setSelectedLocationType}
				/>
			),
		};
	};

	const getFeedingSubjectInputRow = (): FormRow => {
		return {
			name: localized('RecipeName'),
			component: <SkioldFormInput onChangeText={setRecipeName} text={recipeName} />,
		};
	};

	const getFormRows = () => {
		let formRows = new Array<FormRow>();

		formRows.push(getWorkListHeader());
		formRows.push(getAnimalTypeRow());
		formRows.push(getLocationTypeRow());
		formRows.push(getFeedingSubjectInputRow());
		return formRows;
	};

	const saveRecipe = () => {
		if (selectedOption?.value === undefined) {
			showAlert(localized('selectAnimalKind'));
			return;
		}

		dispatch(
			upsertRecipe({
				id: recipe?.id,
				name: recipeName,
				animalType: selectedOption?.value as AnimalType,
				isSkioldDigitalRecipe: true,
				siteId,
				locationType: selectedLocationType?.value as LocationType,
			} as IRecipeDto),
		);
		closeModal && closeModal();
	};

	return (
		<ViewWeb className="create-recipe">
			<SkioldFormsWrapper formRows={getFormRows()} containerClassName="wrapper-container" />
			<div>
				<SkioldButton title={localized('Save')} onPress={saveRecipe} />
				<SkioldButton theme="grey" title={localized('Close')} onPress={closeModal} />
			</div>
		</ViewWeb>
	);
});
